/**
 * Exercise CONTENT language — which language the backend resolves exercise names, descriptions and
 * instructions into. Deliberately independent of the app's interface language: a Polish trainer may
 * want the library in English while the chrome stays Polish. It goes on the WIRE and nowhere else;
 * `i18n.language` remains what words, formats and collates everything the reader sees.
 *
 * Ported from the mobile app's `features/exercises/model/contentLanguage.ts`. Two properties carry
 * over, and both are why the stored value is an OVERRIDE rather than a language:
 *
 * · `null` means "follow the interface language", so a trainer who never opens the control keeps
 *   following it — including after they later switch the app to English.
 * · Choosing the language the app is ALREADY in clears the override rather than pinning it. Pressing
 *   „Polski" on a Polish app is a no-op to the trainer and must not opt them out silently.
 *
 * `useContentLanguage()` is the subscription; `contentLanguage()` is the synchronous read for call
 * sites that run after an await — a mutation body, a cache key in a callback. Both resolve through
 * the same function, so the two cannot disagree.
 */

import { useSyncExternalStore } from 'react';

import { i18next, resolveLanguage } from './index';
import { preferenceStore, type PreferenceStore } from '../lib/preferenceStore';

/** The two languages the backend's `lang` query parameter accepts (exercise-localization R1). */
export type ContentLanguage = 'pl' | 'en';

export const CONTENT_LANGUAGES: readonly ContentLanguage[] = ['pl', 'en'];

/** Where the choice is remembered. Namespaced like `movebuddy.theme` and `movebuddy.language`. */
export const CONTENT_LANGUAGE_STORAGE_KEY = 'movebuddy.contentLanguage';

/** Anything else on disk reads as "no override", the state a first visit is in. Never throws. */
export function parseContentLanguageOverride(raw: string | null): ContentLanguage | null {
  return raw === 'pl' || raw === 'en' ? raw : null;
}

/**
 * The content language actually in force. `resolveLanguage` reduces a regional tag (`pl-PL` → `pl`)
 * and sends everything non-Polish to English, matching the backend's hard-EN default.
 */
export function effectiveContentLanguage(
  override: ContentLanguage | null,
  interfaceLanguage: string,
): ContentLanguage {
  return override ?? resolveLanguage(interfaceLanguage);
}

/**
 * The content language that is NOT in force — what the exercise search reads a second catalogue in,
 * so a Polish name finds an English-listed movement and the reverse (the phone searches both).
 */
export function otherContentLanguage(lang: ContentLanguage): ContentLanguage {
  return lang === 'pl' ? 'en' : 'pl';
}

/** What to store when the trainer picks `selected`. See the header for why this can be `null`. */
export function nextContentLanguageOverride(
  selected: ContentLanguage,
  interfaceLanguage: string,
): ContentLanguage | null {
  return selected === effectiveContentLanguage(null, interfaceLanguage) ? null : selected;
}

/* ── The store ──────────────────────────────────────────────────────────────────────────────── */

const listeners = new Set<() => void>();

function readOverride(store: PreferenceStore | null): ContentLanguage | null {
  if (store === null) return null;
  try {
    return parseContentLanguageOverride(store.getItem(CONTENT_LANGUAGE_STORAGE_KEY));
  } catch {
    // A read that throws is not a reason to invent a language: fall through to `null`, which is
    // what a trainer who never touched the control already gets.
    return null;
  }
}

/** Seeded synchronously, so the session's first request already carries the right `lang`. */
let override: ContentLanguage | null = readOverride(preferenceStore());

/**
 * The language every exercise-bearing request sends, for callers outside React.
 *
 * `i18next.language` is `undefined` until `initI18n()` resolves; a module read during boot can get
 * there first, so the empty string stands in and resolves to English, the backend's own default.
 */
export function contentLanguage(): ContentLanguage {
  return effectiveContentLanguage(override, i18next.language ?? '');
}

/** The stored override itself — what the Aplikacja tab's control is currently sitting on. */
export function contentLanguageOverride(): ContentLanguage | null {
  return override;
}

export function setContentLanguage(selected: ContentLanguage): void {
  override = nextContentLanguageOverride(selected, i18next.language ?? '');
  for (const listener of listeners) listener();

  const store = preferenceStore();
  if (store === null) return;
  try {
    // A cleared override is stored as the empty string rather than removed: a third `PreferenceStore`
    // method for this one caller would widen a shape every preference takes. It parses back to `null`.
    store.setItem(CONTENT_LANGUAGE_STORAGE_KEY, override ?? '');
  } catch {
    // A trainer who cannot persist the choice still gets it for this session.
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  // i18next's own change is a second reason the effective language moves: while no override is set,
  // switching the interface language switches the content language with it.
  i18next.on('languageChanged', listener);
  return () => {
    listeners.delete(listener);
    i18next.off('languageChanged', listener);
  };
}

/** The language a component's reads should send. Re-renders when either half of it moves. */
export function useContentLanguage(): ContentLanguage {
  return useSyncExternalStore(subscribe, contentLanguage, contentLanguage);
}

/** The stored override, for the one control that sets it. `null` while it follows the app. */
export function useContentLanguageOverride(): ContentLanguage | null {
  return useSyncExternalStore(subscribe, contentLanguageOverride, contentLanguageOverride);
}
